type CryptoLike = Pick<Crypto, 'getRandomValues'> & {
  randomUUID?: () => string;
};

function getWebCrypto(): CryptoLike | undefined {
  if (typeof globalThis === 'undefined' || !('crypto' in globalThis)) return undefined;
  return globalThis.crypto as CryptoLike;
}

/**
 * Creates an RFC 4122 version 4 UUID on every supported browser origin.
 *
 * `crypto.randomUUID` is restricted to secure contexts, so it is absent when
 * the app is opened over plain HTTP via a LAN IP. `getRandomValues` remains
 * available there; the Math.random branch is only a last-resort fallback for
 * environments without Web Crypto at all (such as a constrained test/runtime).
 */
export function createUuid(): string {
  const webCrypto = getWebCrypto();
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  // RFC 4122 §4.4: version 4 and variant 10xx.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
